import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AddressMonikerResponse } from 'services/address';
import { Address, Perils } from 'types/address';
import {
  Industry,
  Occupation,
  OccupationCategory,
  OccupationSearchType,
  OccupationsListItem,
} from 'types/refData';

import { BusinessDetails, InsuredType, PropertyType } from './types';

const initialState: Partial<BusinessDetails> = {
  coverStart: JSON.stringify(new Date()),
  dutyOfDisclosure: false,
  /** @note isValidAddress and isVerifiedAddress are from backend responses with moniker */
  isValidAddress: false,
  isVerifiedAddress: false,
  isSpecificBusinessDetailsValid: false,
  isFloodZone: false,
  showAllOccupationCategories: false,
  isManualEntry: false,
  occupationSearchType: OccupationSearchType.Icon,
  updateList: [],
  version: 0,
};

export const businessDetailsSlice = createSlice({
  name: 'businessDetails',
  initialState,
  reducers: {
    /*
     * Redux Toolkit allows us to write "mutating" logic in reducers. It doesn't actually mutate the state
     * because it uses the Immer library, which detects changes to a "draft state" and produces a brand new
     * immutable state based off those changes
     */
    updateCoverStart: (state, action: PayloadAction<string>) => {
      state.coverStart = action.payload;
    },
    updateDutyOfDisclosure: (state, action: PayloadAction<boolean>) => {
      state.dutyOfDisclosure = action.payload;
    },
    updateIndustry: (state, action: PayloadAction<Industry | undefined>) => {
      if (state.industry !== action.payload) {
        businessDetailsSlice.caseReducers.clearOccupationCategory(state);
        state.industry = action.payload;
      }
    },
    clearIndustry: (state) => {
      state.industry = undefined;
      businessDetailsSlice.caseReducers.clearOccupationCategory(state);
    },
    updateIndustryOnly: (state, action: PayloadAction<Industry | undefined>) => {
      state.industry = action.payload;
    },
    updateOccupationCategory: (state, action: PayloadAction<OccupationCategory | undefined>) => {
      if (state.occupationCategory !== action.payload) {
        businessDetailsSlice.caseReducers.clearOccupationCategory(state);
        state.occupationCategory = action.payload;
      }
    },
    clearOccupationCategory: (state) => {
      state.occupationCategory = undefined;
      businessDetailsSlice.caseReducers.clearOccupation(state);
    },
    updateOccupationCategoryOnly: (
      state,
      action: PayloadAction<OccupationCategory | undefined>
    ) => {
      state.occupationCategory = action.payload;
    },
    updateOccupation: (state, action: PayloadAction<Occupation | undefined>) => {
      state.occupation = action.payload;
      if (action.payload && state.occupationItem?.code !== action.payload.anzsicCode) {
        state.occupationItem = state.occupationsList?.find(
          (o) => o.code === action.payload?.anzsicCode
        );
      } else if (!action.payload) {
        state.occupationItem = undefined;
      }
    },
    updateOccupationItem: (state, action: PayloadAction<OccupationsListItem | undefined>) => {
      state.occupationItem = action.payload;
    },
    clearOccupation: (state) => {
      state.occupation = undefined;
      state.propertyOwnerOccupation = undefined;
    },
    updateOccupationsList: (state, action: PayloadAction<OccupationsListItem[] | undefined>) => {
      state.occupationsList = action.payload;
    },
    updateEmployeeCount: (state, action: PayloadAction<number | undefined>) => {
      state.employeeCount = action.payload;
    },
    updateAnnualTurnover: (state, action: PayloadAction<number | undefined>) => {
      state.annualTurnover = action.payload;
    },
    updateBusinessAddress: (state, action: PayloadAction<Address | undefined>) => {
      state.businessAddress = action.payload;
    },
    updateFullBusinessAddress: (
      state,
      action: PayloadAction<AddressMonikerResponse | undefined>
    ) => {
      state.fullBusinessAddress = action.payload;
    },
    updateAddressLine1: (state, action: PayloadAction<string | undefined>) => {
      state.addressLine1 = action.payload;
    },
    updateSuburb: (state, action: PayloadAction<string | undefined>) => {
      state.suburb = action.payload;
    },
    updateState: (state, action: PayloadAction<string | undefined>) => {
      state.state = action.payload;
    },
    updatePostcode: (state, action: PayloadAction<string | undefined>) => {
      state.postcode = action.payload;
    },
    updateIsValidAddress: (state, action: PayloadAction<boolean | undefined>) => {
      state.isValidAddress = action.payload;
    },
    updateIsVerifiedAddress: (state, action: PayloadAction<boolean | undefined>) => {
      state.isVerifiedAddress = action.payload;
    },
    updatePerils: (state, { payload: perils }: PayloadAction<Perils | undefined>) => {
      state.perils = perils;
      if (!perils) {
        state.isFloodZone = false;
        return;
      }
      const floodRating = state.industry?.limits?.floodRatingZone;
      state.isFloodZone = floodRating
        ? perils.flood.buildingRiskGroup > floodRating &&
          perils.flood.contentsRiskGroup > floodRating
        : false;
    },
    updateShowAllOccupationCategories: (state, action: PayloadAction<boolean>) => {
      state.showAllOccupationCategories = action.payload;
    },
    updateOccupationSearchType: (state, action: PayloadAction<OccupationSearchType>) => {
      state.occupationSearchType = action.payload;
    },
    updateInsuredType: (state, action: PayloadAction<InsuredType | undefined>) => {
      if (state.insuredType !== action.payload) {
        businessDetailsSlice.caseReducers.clearPropertyType(state);
        businessDetailsSlice.caseReducers.clearSpecificDetails(state);
        state.insuredType = action.payload;
      }
    },
    clearInsuredType: (state) => {
      state.insuredType = undefined;
      businessDetailsSlice.caseReducers.clearPropertyType(state);
    },
    updatePropertyType: (state, action: PayloadAction<PropertyType | undefined>) => {
      if (state.propertyType !== action.payload) {
        businessDetailsSlice.caseReducers.clearIndustry(state);
        businessDetailsSlice.caseReducers.clearSpecificDetails(state);
        state.propertyType = action.payload;
      }
    },
    clearPropertyType: (state) => {
      state.propertyType = undefined;
      businessDetailsSlice.caseReducers.clearIndustry(state);
    },
    updatePropertyValue: (state, action: PayloadAction<number | undefined>) => {
      state.propertyValue = action.payload;
    },
    updatePropertyOwnerOccupation: (
      state,
      action: PayloadAction<OccupationsListItem | undefined>
    ) => {
      state.propertyOwnerOccupation = action.payload;
    },
    clearManualAddress: (state) => {
      state.addressLine1 = undefined;
      state.suburb = undefined;
      state.state = undefined;
      state.postcode = undefined;
    },
    clearPerils: (state) => {
      state.isValidAddress = false;
      state.isVerifiedAddress = false;
      state.perils = undefined;
      state.isFloodZone = false;
    },
    clearLookedupAddress: (state) => {
      state.businessAddress = undefined;
      state.fullBusinessAddress = undefined;
    },
    clearSpecificDetails: (state) => {
      state.employeeCount = undefined;
      state.annualTurnover = undefined;
      state.propertyValue = undefined;
      businessDetailsSlice.caseReducers.clearPerils(state);
      businessDetailsSlice.caseReducers.clearLookedupAddress(state);
    },
    updateIsManualEntry: (state, action: PayloadAction<boolean>) => {
      state.isManualEntry = action.payload;
    },
  },
});

export default businessDetailsSlice.reducer;
